import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useGraphneAPI, ResourceDetailsView, ResponseError } from '@nexelem/graphne-api';
import { GRAPHNE_RESOURCE_GROUP_LIST_TIMEFRAME_KEY } from './useGraphneResourcesGroupTimeframe';
import { useCustomToast } from '@nexelem/snackbar';
import { useCustomTranslation } from '../../hooks';
import { Trans } from 'react-i18next';
import { GRAPHNE_RESOURCE_SOFT_ASSIGNMENT_TASK_ID_KEY } from './useGraphneResourcesSoftAssignmentTaskId';
import { taskDetailsQueryKey } from './useGraphneTaskDetails';

type GraphneResourcesSoftAssignMutationProps = {
  task: {
    id: number;
    name: string;
  };
  resourceIds: number[];
  workersData: Pick<ResourceDetailsView, 'id' | 'name'>[];
  validateAvailability: boolean;
};

export const useGraphneResourcesSoftAssignMutation = (isBatch?: boolean) => {
  const api = useGraphneAPI();
  const queryClient = useQueryClient();
  const displayToast = useCustomToast();
  const { tWrapper } = useCustomTranslation('products.productionPlan.assignWorkerModal');

  return useMutation({
    onSuccess: (_result, { task, workersData }) => {
      queryClient.invalidateQueries({ queryKey: [GRAPHNE_RESOURCE_GROUP_LIST_TIMEFRAME_KEY] });
      queryClient.invalidateQueries({ queryKey: [GRAPHNE_RESOURCE_SOFT_ASSIGNMENT_TASK_ID_KEY] });
      queryClient.invalidateQueries({ queryKey: taskDetailsQueryKey(task.id) });
      const workerNames = workersData.map(({ name }) => name).join(', ');
      displayToast({
        toastType: 'success',
        message: (
          <Trans components={{ strong: <strong /> }}>
            {tWrapper('assignSuccess', { productName: task.name, workers: workerNames })}
          </Trans>
        ),
      });
    },
    onError: (error: ResponseError) => {
      if (error.response.status !== 400) {
        displayToast({
          toastType: 'error',
          message: tWrapper('assignError'),
        });
      }
    },
    mutationFn: ({ task, resourceIds, validateAvailability }: GraphneResourcesSoftAssignMutationProps) => {
      if (isBatch) {
        return api.postGraphneResourcesSoftV2AssignBatch({
          softResourceBatchAssignmentV2: {
            batchId: task.id,
            resources: resourceIds,
            validateAvailability,
          },
        });
      }

      return api.postGraphneResourcesSoftV2Assign({
        softResourceAssignmentV2: {
          taskId: task.id,
          resources: resourceIds,
          validateAvailability,
        },
      });
    },
  });
};
